import { useMemo, useRef, useEffect } from "react";
import { TradeToEarnLeaderboardDto } from "@cede/types";
import { useLeaderboard } from "@/hooks/campaign/useLeaderboard";
import { useHubUser } from "@/hooks/user/useHubUser";
import { hasUserVolumeChanged } from "@/hooks/volumeTracker/utils";
import { getUserVolumeInWindow } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";

type UseUserVolumeInWindowProps = {
  campaignName: string;
  /** Epoch object with id property. Uses minimal type { id: number } to accommodate Zod 4 type inference differences for nested optional fields. */
  epoch?: { id: number };
  enabled?: boolean;
};

const QUERY_KEY = "userVolumeInWindow";

export const useUserVolumeInWindow = ({ campaignName, epoch, enabled = true }: UseUserVolumeInWindowProps) => {
  const { auth, hubUser } = useHubUser();

  /**
   * @remarks
   * We track the previous leaderboard to avoid unnecessary refetches
   */
  const previousLeaderboard = useRef<TradeToEarnLeaderboardDto | undefined>(undefined);
  // Only call useLeaderboard when this hook is enabled
  const { leaderboard } = useLeaderboard(campaignName, enabled ? epoch : undefined);

  const {
    data,
    refetch: refetchUserVolumeInWindow,
    isLoading,
  } = useQuery({
    queryKey: [QUERY_KEY, campaignName],
    queryFn: () => {
      return getUserVolumeInWindow(campaignName, auth);
    },
    enabled: !!auth && enabled,
  });

  /**
   * Refetch user volume when leaderboard changes and user's volume has changed
   */
  useEffect(() => {
    if (leaderboard && previousLeaderboard.current && hubUser) {
      if (hasUserVolumeChanged(hubUser.id, previousLeaderboard.current, leaderboard)) {
        refetchUserVolumeInWindow();
      }
    }
    previousLeaderboard.current = leaderboard;
  }, [leaderboard, hubUser, refetchUserVolumeInWindow]);

  // memoize here is used to avoid unnecessary re-renders in further components
  const memoizedNextWindowDate = useMemo(() => {
    return data?.nextWindowDate ? new Date(data.nextWindowDate) : undefined;
  }, [data?.nextWindowDate]);

  return {
    userVolumeInWindow: data?.currentWindowVolume ?? new Decimal(0),
    nextWindowDate: memoizedNextWindowDate,
    refetchUserVolumeInWindow,
    isLoading,
  };
};
